import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api-client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { StatusBadge } from "@/components/badges/StatusBadge";
import { TemperaturePill } from "@/components/badges/TemperaturePill";
import { ChannelBadge } from "@/components/badges/ChannelBadge";
import { CrossChannelMemoryPanel } from "@/components/contacts/CrossChannelMemoryPanel";
import { formatIdr, formatRelative, formatAbsolute } from "@/lib/format";
import { useToast } from "@/lib/toast";
import { Loader2, ArrowLeft, Pencil, Check, X } from "lucide-react";
import type { Channel, ContactStatus, LeadTemperature } from "@ai-leasing-os/shared";

interface Detail {
  contact: {
    id: string;
    display_name: string | null;
    primary_phone: string | null;
    primary_email: string | null;
    nationality: string | null;
    preferred_contact_language: string | null;
    status: ContactStatus;
    lost_reason: string | null;
    score: number;
    awareness_level: string;
    temperature: LeadTemperature;
    extracted_budget_min_idr: number | null;
    extracted_budget_max_idr: number | null;
    extracted_move_in_date: string | null;
    extracted_length_months: number | null;
    extracted_preferred_area: string | null;
    extracted_bedrooms: number | null;
    internal_notes: string | null;
    tags: string[] | string | null;
    last_signal_at: string | null;
    created_at: string;
    updated_at: string;
  };
  identities: Array<{
    id: string;
    channel: Channel;
    external_id: string;
    display_name: string | null;
    username: string | null;
    phone: string | null;
    created_at: string;
  }>;
  conversations: Array<{
    id: string;
    channel: Channel;
    status: string;
    human_takeover: number;
    source_platform: string | null;
    last_message_at: string | null;
    last_inbound_at: string | null;
  }>;
}

export function ContactDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { data, isLoading } = useQuery({
    queryKey: ["contact", id],
    queryFn: () => api.get<Detail>(`/api/contacts/${id}`),
    enabled: Boolean(id),
    refetchInterval: 15_000,
  });

  if (isLoading || !data) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
      </div>
    );
  }
  const c = data.contact;

  return (
    <div className="space-y-4 max-w-5xl">
      <Link to="/contacts" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900">
        <ArrowLeft className="h-4 w-4" /> Back to contacts
      </Link>

      <Card>
        <CardContent className="py-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <EditableContactName contactId={c.id} name={c.display_name} />
              <div className="text-sm text-slate-500 mt-1">
                {c.primary_phone ?? "-"}
                {c.primary_email && ` · ${c.primary_email}`}
                {c.nationality && ` · ${c.nationality}`}
              </div>
              <div className="flex items-center gap-2 mt-3">
                <StatusBadge status={c.status} />
                <TemperaturePill temperature={c.temperature} />
                <span className="text-sm text-slate-500">score <strong>{c.score}</strong></span>
                <span className="text-sm text-slate-500">{c.awareness_level}</span>
              </div>
              {parseTags(c.tags).length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {parseTags(c.tags).map((t) => (
                    <span
                      key={t}
                      className="inline-block rounded bg-brand-soft px-2 py-0.5 text-xs font-medium text-brand-primary"
                    >
                      {t}
                    </span>
                  ))}
                </div>
              )}
            </div>
            <div className="text-right text-xs text-slate-400">
              <div>Created {formatAbsolute(c.created_at)}</div>
              <div>Updated {formatRelative(c.updated_at)}</div>
              {c.last_signal_at && <div>Last signal {formatRelative(c.last_signal_at)}</div>}
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader><CardTitle>Extracted qualification</CardTitle></CardHeader>
          <CardContent className="space-y-1.5 text-sm">
            <Field label="Move-in date" value={c.extracted_move_in_date} />
            <Field label="Length of stay (months)" value={c.extracted_length_months} />
            <Field label="Budget (IDR)" value={formatBudget(c.extracted_budget_min_idr, c.extracted_budget_max_idr)} />
            <Field label="Preferred area" value={c.extracted_preferred_area} />
            <Field label="Bedrooms" value={c.extracted_bedrooms} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>Identities</CardTitle></CardHeader>
          <CardContent>
            {data.identities.length === 0 ? (
              <p className="text-xs text-slate-400">No identities yet.</p>
            ) : (
              <ul className="space-y-2">
                {data.identities.map((i) => (
                  <li key={i.id} className="flex items-center gap-2 text-sm">
                    <ChannelBadge channel={i.channel} />
                    <code className="font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded">
                      {i.external_id}
                    </code>
                    {i.username && <span className="text-slate-500">@{i.username}</span>}
                    {i.phone && <span className="text-slate-500">{i.phone}</span>}
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader><CardTitle>Recent conversations</CardTitle></CardHeader>
        <CardContent>
          {data.conversations.length === 0 ? (
            <p className="text-xs text-slate-400">No conversations.</p>
          ) : (
            <ul className="space-y-1.5">
              {data.conversations.map((cv) => (
                <li key={cv.id} className="flex items-center gap-2 text-sm">
                  <ChannelBadge channel={cv.channel} />
                  <Link to={`/inbox`} className="font-medium text-slate-900 hover:underline">
                    {cv.id.slice(-8)}
                  </Link>
                  <span className="text-xs text-slate-500">{cv.status}</span>
                  {cv.human_takeover === 1 && <span className="text-xs text-amber-700">takeover</span>}
                  {cv.source_platform === "marketplace" && <span className="text-xs text-teal-700">marketplace</span>}
                  <span className="ml-auto text-xs text-slate-400">{formatRelative(cv.last_message_at)}</span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* Phase H7 - Cross-channel memory bundle (read-only). Hits
          `GET /api/contacts/:id/memory`. Empty states render compact
          "-" placeholders so this stays out of the way for fresh
          contacts. */}
      {c.id && <CrossChannelMemoryPanel contactId={c.id} />}

      {c.internal_notes && (
        <Card>
          <CardHeader><CardTitle>Internal notes</CardTitle></CardHeader>
          <CardContent className="text-sm whitespace-pre-wrap text-slate-700">
            {c.internal_notes}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

/**
 * Inline-editable contact name. `contacts.display_name` is the single source
 * for the name shown everywhere on the dashboard, so on save we invalidate
 * every query that renders it (contacts list, this detail, inbox list,
 * conversation pane) and the name updates across the app.
 */
function EditableContactName({
  contactId,
  name,
}: {
  contactId: string;
  name: string | null;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(name ?? "");

  const mut = useMutation({
    mutationFn: (display_name: string | null) =>
      api.put<{ id: string; display_name: string | null }>(
        `/api/contacts/${contactId}`,
        { display_name },
      ),
    onSuccess: () => {
      for (const key of [["contact"], ["contacts"], ["conversation"], ["conversations"]]) {
        qc.invalidateQueries({ queryKey: key });
      }
      setEditing(false);
      toast.success("Name updated", "The change is reflected across the dashboard.");
    },
    onError: (e) =>
      toast.error("Could not update name", e instanceof Error ? e.message : undefined),
  });

  function save() {
    const trimmed = value.trim();
    mut.mutate(trimmed.length === 0 ? null : trimmed);
  }

  if (!editing) {
    return (
      <div className="group flex items-center gap-2">
        <h1 className="text-2xl font-semibold text-slate-900">
          {name || "(no name)"}
        </h1>
        <button
          type="button"
          onClick={() => {
            setValue(name ?? "");
            setEditing(true);
          }}
          title="Edit name"
          className="rounded p-1 text-slate-400 opacity-0 transition hover:bg-slate-100 hover:text-slate-700 focus:opacity-100 group-hover:opacity-100"
        >
          <Pencil className="h-4 w-4" />
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <Input
        autoFocus
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") save();
          if (e.key === "Escape") setEditing(false);
        }}
        placeholder="Contact name"
        maxLength={255}
        disabled={mut.isPending}
        className="h-9 max-w-xs text-lg font-semibold"
      />
      <Button size="sm" variant="primary" onClick={save} disabled={mut.isPending}>
        {mut.isPending ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <Check className="h-4 w-4" />
        )}
        Save
      </Button>
      <Button
        size="sm"
        variant="ghost"
        onClick={() => setEditing(false)}
        disabled={mut.isPending}
        title="Cancel"
      >
        <X className="h-4 w-4" />
      </Button>
    </div>
  );
}

function Field({ label, value }: { label: string; value: unknown }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-slate-500">{label}</span>
      <span className="font-medium text-slate-900">
        {value === null || value === undefined ? "-" : String(value)}
      </span>
    </div>
  );
}

function formatBudget(min: number | null, max: number | null): string {
  if (min == null && max == null) return "-";
  if (min == null) return `≤ ${formatIdr(max)}`;
  if (max == null) return `≥ ${formatIdr(min)}`;
  return `${formatIdr(min)} - ${formatIdr(max)}`;
}

/** TiDB returns JSON columns as a parsed array OR a raw string. Normalize. */
function parseTags(raw: string[] | string | null): string[] {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.filter((t) => typeof t === "string");
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((t) => typeof t === "string") : [];
  } catch {
    return [];
  }
}
